// My Shifts — the employee landing page. Hero card for the next upcoming
// shift (with acknowledge nudge), current-week hours meter, then a 14-day
// agenda. Reads are one-shot React Query fetchers; the cache persists so this
// renders offline with an "as of" line.
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useAuth } from '../../auth/AuthProvider'
import { fetchMyShifts, fetchPositions, fetchTeams } from '../../api/reads'
import { api } from '../../api/mutations'
import { useToast } from '../../components/Toast'
import { EmptyState, ErrorBanner, Spinner } from '../../components/ui'
import { HoursMeter } from '../../components/time'
import { ShiftPill } from '../../components/ShiftPill'
import { addDays, todayLocal, weekKeyFor, TZ } from '../../lib/time'
import { formatDateLong, formatHours, formatTimeRange } from '../../lib/format'
import { positionColor } from '../../lib/positionColors'
import { round2 } from '../../lib/hours'
import type { Employee, Position, Shift, Team } from '../../lib/types'

export default function MyShiftsPage() {
  const { employee, email } = useAuth()
  if (!employee || !email) return null
  return <MyShifts employee={employee} email={email} />
}

/** "in 3h 20m" / "in 2d 5h" / "Happening now" from the true UTC start. */
function countdownLabel(startAt: string, endAt: string, nowMs: number): string {
  const start = Date.parse(startAt)
  const end = Date.parse(endAt)
  if (nowMs >= start && nowMs < end) return 'Happening now'
  const totalMinutes = Math.max(0, Math.round((start - nowMs) / 60_000))
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `in ${days}d ${hours}h`
  if (hours > 0) return `in ${hours}h ${minutes}m`
  return `in ${minutes}m`
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])
  return online
}

function MyShifts({ employee, email }: { employee: Employee; email: string }) {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const online = useOnline()
  const today = todayLocal()
  const departmentId = employee.departmentId

  // Minute tick so the countdown stays honest while the tab sits open.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => window.clearInterval(t)
  }, [])

  const shiftsQuery = useQuery({
    queryKey: ['myShifts', email],
    queryFn: () => fetchMyShifts(email, todayLocal()),
  })

  const referenceQuery = useQuery({
    queryKey: ['reference', departmentId],
    queryFn: async () => {
      const [teams, positions] = await Promise.all([fetchTeams(departmentId!), fetchPositions(departmentId!)])
      return { teams, positions }
    },
    enabled: !!departmentId,
  })

  const teamsById = useMemo(
    () => new Map<string, Team>((referenceQuery.data?.teams ?? []).map((t) => [t.id, t])),
    [referenceQuery.data],
  )
  const positionsById = useMemo(
    () => new Map<string, Position>((referenceQuery.data?.positions ?? []).map((p) => [p.id, p])),
    [referenceQuery.data],
  )

  const [acking, setAcking] = useState(false)
  const acknowledge = async (shiftId: string) => {
    setAcking(true)
    try {
      await api.acknowledgeShift({ shiftId })
      await queryClient.invalidateQueries({ queryKey: ['myShifts', email] })
      toast('Shift confirmed. Thanks!', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not confirm the shift.', 'error')
    } finally {
      setAcking(false)
    }
  }

  if (shiftsQuery.isPending) return <Spinner label="Loading your shifts…" />
  if (shiftsQuery.isError) {
    return <ErrorBanner message="Couldn't load your shifts. Check your connection and try again." />
  }

  const shifts = shiftsQuery.data
  const sorted = [...shifts].sort((a, b) => a.startAt.localeCompare(b.startAt))
  const nextShift = sorted.find((s) => Date.parse(s.endAt) > nowMs) ?? null

  // Current-week hours (weekId of the shift's start day — the attribution rule).
  const currentWeek = weekKeyFor(today)
  const weekHours = round2(
    shifts.filter((s) => s.weekId === currentWeek).reduce((sum, s) => sum + s.durationHours, 0),
  )
  const threshold = employee.otThresholdHours ?? 40

  // 14-day agenda, grouped by date.
  const horizon = addDays(today, 13)
  const agendaShifts = sorted.filter((s) => s.date <= horizon)
  const byDate = new Map<string, Shift[]>()
  for (const s of agendaShifts) {
    const list = byDate.get(s.date) ?? []
    list.push(s)
    byDate.set(s.date, list)
  }
  const agendaDates = [...byDate.keys()].sort()

  const colorFor = (s: Shift) =>
    positionColor(positionsById.get(s.positionId) ?? { color: null, id: s.positionId })

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <h1 className="sr-only">My shifts</h1>

      {nextShift ? (
        <section className="card space-y-3" aria-label="Next shift">
          <div className="flex items-baseline justify-between gap-2">
            <span className="section-label">Next shift</span>
            <span className="tnum text-sm font-semibold text-clay">
              {countdownLabel(nextShift.startAt, nextShift.endAt, nowMs)}
            </span>
          </div>
          <div>
            <p className="text-xl font-bold text-interlochen">{formatDateLong(nextShift.date)}</p>
            <p className="tnum text-lg font-semibold text-ink">
              {formatTimeRange(nextShift.startTime, nextShift.endTime, false)}
              {nextShift.endsNextDay && (
                <span className="chip ml-2 bg-sky/40 text-interlochen" title="Ends the next day">
                  +1d
                </span>
              )}
              <span className="tnum ml-2 text-sm font-normal text-stone">
                {formatHours(nextShift.durationHours)}
              </span>
            </p>
            <p className="text-ink">
              {nextShift.positionName}
              {teamsById.get(nextShift.teamId) && (
                <span className="text-stone"> · {teamsById.get(nextShift.teamId)!.name}</span>
              )}
            </p>
          </div>
          {nextShift.ackedAt === null && (
            <div className="flex flex-col gap-2 rounded-md border border-sunset/50 bg-sunset/10 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-clay">New/changed — please confirm</p>
              <button
                className="btn-primary !min-h-[40px]"
                onClick={() => void acknowledge(nextShift.id)}
                disabled={acking}
              >
                {acking ? 'Confirming…' : 'Acknowledge'}
              </button>
            </div>
          )}
          <button className="btn-ghost !min-h-[40px] self-start px-2" onClick={() => void navigate(`/shift/${nextShift.id}`)}>
            View details ›
          </button>
        </section>
      ) : (
        <EmptyState title="No upcoming shifts." hint="Your published shifts will show up here." />
      )}

      <section className="card" aria-label="Hours this week">
        <HoursMeter hours={weekHours} threshold={threshold} />
      </section>

      {!online && (
        <p className="text-center text-xs text-stone">
          Offline — showing shifts as of{' '}
          {DateTime.fromMillis(shiftsQuery.dataUpdatedAt).setZone(TZ).toFormat('ccc, LLL d, h:mma').toLowerCase()}
        </p>
      )}

      {agendaDates.length > 0 && (
        <section aria-label="Next 14 days">
          <h2 className="section-label mb-2">Next 14 days</h2>
          <div className="space-y-4">
            {agendaDates.map((date) => (
              <div key={date}>
                <h3 className="mb-1.5 text-sm font-bold text-interlochen">{formatDateLong(date)}</h3>
                <ul className="space-y-1.5">
                  {byDate.get(date)!.map((s) => (
                    <li key={s.id}>
                      <ShiftPill
                        shift={s}
                        positionColor={colorFor(s)}
                        showPosition
                        onClick={() => void navigate(`/shift/${s.id}`)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
